import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Button } from "antd";
import { LinkOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import API from "../../common/api/axios";
import { TENANT_HEADERS, refreshDurationMs } from "../../common/config";
import {
  formatPutGateLabel,
  normalizePutGate,
  sortGatesByLocation,
} from "../../common/utils/putGate";
import { cleanScan } from "../../common/utils/scan";
import { isAlreadyCompleteError, serverErrorMessage } from "../../common/utils/errors";
import { toastError, toastSuccess } from "../../common/utils/toast";
import StepShell from "../../components/StepShell";
import StatusBanner from "../../components/StatusBanner";
import ScanField from "../../components/ScanField";
import { colors } from "../../app/styles";

type GateState = "disabled" | "actionRequired" | "mapped" | "empty";

interface PutWallGate {
  putWallId?: string;
  location?: string;
  isDisabled?: boolean;
  currentOrder?: string;
  orderToteId?: string;
  mappedPtlDeviceId?: string;
}

const isActiveMappingTask = (task: any): boolean =>
  !!task?.id &&
  !!task?.orderToteMappingTask &&
  task?.isComplete !== true &&
  task?.isCancelled !== true &&
  task?.isAborted !== true;

// HJI's put wall is physically two columns wide, so the handheld lays the gates
// out on the same 2-wide grid (filling left→right, top→bottom in gate order)
// instead of a flat list. Operators use that spatial arrangement as the cue to
// find a gate on the wall.
const PUT_WALL_GRID_COLUMNS = 2;

const LayoutHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const LayoutTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${colors.primaryDark};
`;

const GateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${PUT_WALL_GRID_COLUMNS}, minmax(0, 1fr));
  gap: 10px;
`;

const GateCard = styled.button<{ $state: GateState }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-height: 108px;
  padding: 12px;
  border-radius: 12px;
  text-align: left;
  background: #ffffff;
  border: ${(p) =>
    p.$state === "actionRequired"
      ? `2px dashed ${colors.primary}`
      : `1px solid ${colors.cardBorder}`};
  opacity: ${(p) => (p.$state === "disabled" ? 0.55 : 1)};
  cursor: ${(p) => (p.$state === "actionRequired" ? "pointer" : "default")};

  &:active {
    transform: ${(p) => (p.$state === "actionRequired" ? "scale(0.99)" : "none")};
  }
`;

const GateTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
`;

const GateName = styled.div<{ $state: GateState }>`
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.03em;
  color: ${(p) =>
    p.$state === "actionRequired" ? colors.textMuted : colors.primaryDark};
`;

const GateOrder = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${colors.primaryDark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GateFoot = styled.div<{ $action?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
  font-size: 13px;
  font-weight: ${(p) => (p.$action ? 600 : 700)};
  color: ${(p) => (p.$action ? colors.primary : colors.textMuted)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .tote {
    color: ${colors.primary};
    font-weight: 700;
  }
`;

const Tag = styled.span<{ $state: GateState }>`
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  border-radius: 6px;
  padding: 3px 7px;
  color: ${(p) => (p.$state === "actionRequired" ? "#ffffff" : colors.textMuted)};
  background: ${(p) =>
    p.$state === "actionRequired"
      ? colors.primary
      : p.$state === "mapped"
      ? "#e0fbee"
      : "#eef1f0"};
`;

const Empty = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: ${colors.textMuted};
  font-size: 14px;
`;

const stateTag: Record<GateState, string> = {
  actionRequired: "REQUIRED",
  mapped: "MAPPED",
  disabled: "DISABLED",
  empty: "EMPTY",
};

export default function OrderToteMapping() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<any[]>([]);
  const [gates, setGates] = useState<PutWallGate[]>([]);
  const [defaultPutWallId, setDefaultPutWallId] = useState<string>("");
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<{ task: any; gateLabel: string } | null>(
    null
  );
  const [toteValue, setToteValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await API.get("PickStation/orderToteMappingTasks", {
        headers: TENANT_HEADERS,
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        setTasks(res.data);
      }
    } catch {
      // keep prior list on transient error
    }
  }, []);

  const fetchPutWalls = useCallback(async () => {
    try {
      const res = await API.get("/PutWall", {
        headers: { Accept: "application/json" },
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        const putwalls = res.data as any[];
        const flat: PutWallGate[] = putwalls.flatMap((pw: any) =>
          Array.isArray(pw?.gates)
            ? pw.gates.map((g: any) => ({
                ...g,
                putWallId: g?.putWallId ?? pw?.putWallId ?? pw?.id ?? "",
              }))
            : []
        );
        setDefaultPutWallId(putwalls[0]?.putWallId ?? putwalls[0]?.id ?? "");
        setGates(flat);
      }
    } catch {
      // /PutWall returns [] when the feature is off server-side; tolerate errors
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchPutWalls();
    const interval = setInterval(() => {
      fetchTasks();
      fetchPutWalls();
    }, refreshDurationMs());
    return () => clearInterval(interval);
  }, [fetchTasks, fetchPutWalls]);

  const visibleTasks = tasks.filter(
    (task) => isActiveMappingTask(task) && !completedIds.has(task.id)
  );

  const tasksByGate = new Map<string, any>();
  visibleTasks.forEach((task) => {
    const gate = normalizePutGate(task?.orderToteMappingTask?.gate);
    if (gate && !tasksByGate.has(gate)) {
      tasksByGate.set(gate, task);
    }
  });

  const sortedGates = gates.slice().sort(sortGatesByLocation);

  const openScan = (task: any, gateLabel: string) => {
    setToteValue("");
    setSelected({ task, gateLabel });
  };

  const closeScan = () => {
    if (submitting) {
      return;
    }
    setSelected(null);
    setToteValue("");
  };

  const handleConfirm = async () => {
    if (!selected) {
      return;
    }
    const task = selected.task;
    const taskId = task?.id;
    const putWallId = task?.orderToteMappingTask?.putWallId ?? defaultPutWallId;
    const gate = normalizePutGate(
      task?.orderToteMappingTask?.gate ?? selected.gateLabel
    );
    const orderToteId = cleanScan(toteValue);

    if (!orderToteId) {
      toastError("Order tote ID is required");
      return;
    }
    if (!taskId || !putWallId || !gate) {
      toastError("Mapping task is missing put wall or gate");
      return;
    }

    const finishBenign = async () => {
      setCompletedIds((prev) => new Set(prev).add(taskId));
      setTasks((prev) => prev.filter((t) => t?.id !== taskId));
      setSelected(null);
      setToteValue("");
      await Promise.all([fetchPutWalls(), fetchTasks()]);
    };

    try {
      setSubmitting(true);
      await API.put(
        `/PickStation/updateOrderToteMappingTask/${encodeURIComponent(
          taskId
        )}/${encodeURIComponent(putWallId)}/${encodeURIComponent(
          gate
        )}/${encodeURIComponent(orderToteId)}`,
        null,
        { headers: TENANT_HEADERS }
      );
      toastSuccess("Order tote mapping done");
      await finishBenign();
    } catch (error) {
      if (isAlreadyCompleteError(error)) {
        toastSuccess("Already mapped — refreshing");
        await finishBenign();
      } else {
        toastError(serverErrorMessage(error, "Failed to map order tote"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Scan step view.
  if (selected) {
    const mapping = selected.task?.orderToteMappingTask;
    const orderRef = mapping?.orderReferenceId ?? "";
    const gateLabel =
      formatPutGateLabel(mapping?.gate ?? selected.gateLabel) || selected.gateLabel;
    return (
      <StepShell
        title={`Map Tote to ${gateLabel}`}
        subtitle={orderRef ? `Order ${orderRef}` : undefined}
        onBack={closeScan}
        actions={
          <>
            <Button size="large" onClick={closeScan} disabled={submitting}>
              Cancel
            </Button>
            <Button
              size="large"
              type="primary"
              loading={submitting}
              disabled={cleanScan(toteValue) === ""}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </>
        }
      >
        <ScanField
          label="Scan Tote Barcode"
          placeholder="TOTE-XXXXX"
          value={toteValue}
          onChange={setToteValue}
          onScan={() => {
            // A scan/manual Enter must NOT auto-submit. The wedge sends a trailing
            // Enter that can otherwise activate a focused button, so blur the field
            // (rather than focusing Confirm) and let the operator verify the scanned
            // tote, then commit with an explicit Confirm tap.
            (document.activeElement as HTMLElement | null)?.blur();
          }}
          focusSignal={selected.task?.id}
          disabled={submitting}
        />
      </StepShell>
    );
  }

  // Gate list view.
  const hasGates = sortedGates.length > 0;
  const hasBareTasks = !hasGates && visibleTasks.length > 0;

  return (
    <StepShell
      title="Order Tote Mapping"
      subtitle="Map order totes to put-wall gates"
      onBack={() => navigate("/")}
    >
      <StatusBanner tone="warning">
        Pick task ACKs are authorized on the desktop UI. Use this handheld
        only to map order totes to put-wall gates.
      </StatusBanner>

      {hasGates && (
        <>
          <LayoutHeader>
            <LayoutTitle>Put Wall Layout Status</LayoutTitle>
          </LayoutHeader>
          <GateGrid>
            {sortedGates.map((gate) => {
              const gateKey = normalizePutGate(gate.location);
              const task = tasksByGate.get(gateKey);
              const currentOrder = String(gate.currentOrder ?? "").trim();
              const isDisabled = gate.isDisabled === true;
              const isPending = !!task;
              const state: GateState = isDisabled
                ? "disabled"
                : isPending
                ? "actionRequired"
                : currentOrder
                ? "mapped"
                : "empty";
              const mapping = task?.orderToteMappingTask;
              const orderDisplay = isPending
                ? String(mapping?.orderReferenceId ?? "").trim() || "Assign tote"
                : currentOrder || "Empty";
              const toteId = gate.orderToteId || mapping?.orderToteId;
              return (
                <GateCard
                  key={`${gate.putWallId || defaultPutWallId}-${gateKey || gate.location}`}
                  type="button"
                  $state={state}
                  onClick={
                    isPending
                      ? () => openScan(task, formatPutGateLabel(gate.location))
                      : undefined
                  }
                >
                  <GateTop>
                    <GateName $state={state}>
                      {formatPutGateLabel(gate.location) || "PUT-?"}
                    </GateName>
                    <Tag $state={state}>{stateTag[state]}</Tag>
                  </GateTop>
                  <GateOrder title={orderDisplay}>{orderDisplay}</GateOrder>
                  {isPending ? (
                    <GateFoot $action>
                      <LinkOutlined /> Map Order Tote
                    </GateFoot>
                  ) : (
                    <GateFoot>
                      {toteId ? (
                        <>
                          <ShoppingCartOutlined />
                          <span className="tote">{toteId}</span>
                        </>
                      ) : (
                        "—"
                      )}
                    </GateFoot>
                  )}
                </GateCard>
              );
            })}
          </GateGrid>
        </>
      )}

      {hasBareTasks && (
        <GateGrid>
          {visibleTasks.map((task) => {
            const mapping = task.orderToteMappingTask;
            const gateLabel = formatPutGateLabel(mapping?.gate);
            return (
              <GateCard
                key={task.id}
                type="button"
                $state="actionRequired"
                onClick={() => openScan(task, gateLabel)}
              >
                <GateTop>
                  <GateName $state="actionRequired">
                    {gateLabel || "PENDING"}
                  </GateName>
                  <Tag $state="actionRequired">{stateTag.actionRequired}</Tag>
                </GateTop>
                <GateOrder title={mapping?.orderReferenceId}>
                  {mapping?.orderReferenceId}
                </GateOrder>
                <GateFoot $action>
                  <LinkOutlined /> Map Order Tote
                </GateFoot>
              </GateCard>
            );
          })}
        </GateGrid>
      )}

      {!hasGates && !hasBareTasks && (
        <Empty>No pending order tote mappings.</Empty>
      )}
    </StepShell>
  );
}
